// Service layer — mirrors src/aircraft-configuration/aircraft_configuration.ts.
//
// ENVELOPE KEY: this route serves `data`, not `items`.
//
//   app/ata_chapters/api.py:20
//     @router.get("", response_model=Page[schemas.ATAChapterList])
//
// There is NO `response_model_by_alias=False` on that decorator, so
// app/pagination.py's alias applies on the wire:
//
//   class Page(BasePage[T]): fields = {"items": {"alias": "data"}}
//
// Read the DECORATOR for this, never openapi.json — FastAPI generates the
// response schema BY ALIAS regardless of the flag, so openapi reports `data`
// for every paginated route including the ~40% that actually serve `items`.
// Checker: python3 repo/scripts/check_envelope_keys.py --route /ata_chapters
//
// The spread on `resp.data.data` is deliberate and load-bearing beyond the
// readonly-array cast: if the envelope key were ever wrong, spreading
// `undefined` THROWS, which the caller turns into a visible error toast plus an
// empty grid. Returning `rows: undefined` instead crashes DataGridPro inside
// useGridRows ("Cannot read properties of undefined (reading 'length')") — a
// white screen where an error message belongs.
import { AxiosResponse } from "axios";
import { CacheRequestConfig } from "axios-cache-interceptor";
import queryString from "query-string";

import { axios } from "../api";
import type { AeronetV6Response } from "../types";

import type { ATAChapterRow } from "./types";

export async function getATAChapters(
  params: any,
  axiosConfig?: CacheRequestConfig
): Promise<{ total: number; rows: ATAChapterRow[] }> {
  const resp: AxiosResponse<AeronetV6Response<ATAChapterRow>> = await axios.get(
    "/v6/ata_chapters",
    {
      params,
      paramsSerializer: (p) => queryString.stringify(p),
      ...axiosConfig,
      id: "get-ata-chapters-list",
    }
  );
  axios.storage.remove("get-ata-chapters-list");
  return { total: resp.data.total, rows: [...resp.data.data] };
}

/** One chapter (GET /v6/ata_chapters/{id}): the record a `?id=` link opens (ataChapterHref). */
export async function getATAChapter(id: number): Promise<ATAChapterRow> {
  const resp = await axios.get<ATAChapterRow>(`/v6/ata_chapters/${id}`, { id: `get-ata-chapter-${id}` });
  axios.storage.remove(`get-ata-chapter-${id}`);
  return resp.data;
}

export async function createATAChapter(
  data: Partial<ATAChapterRow>
): Promise<ATAChapterRow> {
  const resp = await axios.post("/v6/ata_chapters", data);
  if (axios.storage) {
    axios.storage.remove("get-ata-chapters-list");
  }
  return resp.data;
}

export async function updateATAChapter(
  id: number,
  data: Partial<ATAChapterRow>
): Promise<ATAChapterRow> {
  const resp = await axios.put(`/v6/ata_chapters/${id}`, data);
  if (axios.storage) {
    axios.storage.remove("get-ata-chapters-list");
  }
  return resp.data;
}

export async function deleteATAChapter(id: number): Promise<void> {
  await axios.delete(`/v6/ata_chapters/${id}`);
  if (axios.storage) {
    axios.storage.remove("get-ata-chapters-list");
  }
}
